import React from 'react';
import {Dimensions, ScrollView, StyleSheet, View} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {Icon} from '@rn-vui/themed';
import {observer} from 'mobx-react-lite';

import Button from '../../../components/themed/Button';
import Text from '../../../components/themed/Text';
import ToastBottom from '../../../components/ToastBottom';
import CrossPlatformModal from '../../../components/ui-controls/CrossPlatformModal';
import PressableWithOpacityFeedback from '../../../components/ui-controls/PressableWithOpacityFeedback';
import {InformationContentType} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import {RewardsTermsAndConditionsPopper} from '../../rewards/TermsAndConditions';

export interface ConfirmOrderPlacementModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  /** Include ToastBottom inside modal (mobile) or assume it's outside (web desktop) */
  includeToast?: boolean;
}

/**
 * Confirmation modal shown before placing an order.
 * Displays ID verification requirements for delivery/pickup.
 *
 * Extracted from UnifiedCheckoutScreen and CheckoutPaymentScreen.
 */
export const ConfirmOrderPlacementModal = observer(
  ({
    isVisible,
    onClose,
    onSubmit,
    isSubmitting,
    includeToast = true,
  }: ConfirmOrderPlacementModalProps) => {
    const {cartStore} = useStores();
    const insets = useSafeAreaInsets();

    return (
      <CrossPlatformModal
        isVisible={isVisible}
        hasBackdrop
        backdropOpacity={0.75}
        onBackdropPress={onClose}
        onBackButtonPress={onClose}
        style={styles.modal}>
        <View
          style={[
            styles.container,
            {
              paddingTop: insets.top,
              paddingBottom: insets.bottom,
              height: Dimensions.get('window').height,
            },
          ]}>
          <View style={styles.header}>
            <Text style={styles.heading} variant="heading1">
              PLACE ORDER
            </Text>
            <PressableWithOpacityFeedback onPress={onClose}>
              <Icon name="close" size={30} />
            </PressableWithOpacityFeedback>
          </View>
          <ScrollView style={styles.content}>
            {!!cartStore.isDelivery && (
              <>
                <Text variant="body2" style={styles.confirmText}>
                  The registered customer or other adult nominated to accept the delivery must
                  present a valid form of ID to the delivery person, which will be securely scanned
                  through the delivery person&apos;s app to verify that you are aged 18 or over.
                </Text>
                <Text variant="body2" style={styles.confirmText}>
                  Delivery will be refused if ID is not shown.
                </Text>
                <Text variant="body2" style={styles.confirmText}>
                  By placing this order, you confirm that you are over 18 years old and that you
                  agree to our{' '}
                  <RewardsTermsAndConditionsPopper
                    contentType={InformationContentType.TERMS_AND_CONDITIONS}
                  />
                  .
                </Text>
              </>
            )}
            {!!cartStore.isPickup && (
              <>
                <Text variant="body2" style={styles.confirmText}>
                  The registered customer (18+) who placed the order must have a valid form ID and
                  the credit card used to pay for this order available to show at pick up.
                </Text>
                <Text variant="body2" style={styles.confirmText}>
                  Pick up may be refused if any of these items are not shown.
                </Text>
              </>
            )}
          </ScrollView>
          <View style={styles.actions}>
            <Button loading={isSubmitting} disabled={isSubmitting} onPress={onSubmit}>
              CONFIRM & SUBMIT
            </Button>
            <Button variant="outlined" onPress={onClose}>
              BACK
            </Button>
          </View>
        </View>
        {includeToast ? <ToastBottom /> : null}
      </CrossPlatformModal>
    );
  },
);

const styles = StyleSheet.create({
  modal: {
    padding: 0,
    margin: 0,
  },
  container: {
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
  },
  heading: {
    marginTop: 24,
    marginBottom: 18,
  },
  content: {
    paddingHorizontal: 20,
  },
  confirmText: {
    marginVertical: 8,
  },
  actions: {
    gap: 8,
    flexDirection: 'column',
    padding: 20,
  },
});

export default ConfirmOrderPlacementModal;
